/**
 * Speech-timing service — EAA-1755.
 *
 * Powers the "interactive-reading" content type authoring step. Starts an ASYNC
 * aihub job via the ACH backend `POST /content/speech-timing/generate` (returns
 * `202 {job_id}`) and polls `GET /integrations/jobs/{job_id}` until it completes.
 * The worker forwards the script (text) or an uploaded document (.txt/.docx) to
 * the aihub AI agent (`/api/v3/speech-timing/generate`), which synthesises TTS
 * audio (an S3/CloudFront URL) and per-word timestamps used by the player to
 * highlight words in sync. All AI calls run inside AI Hub as a job; the tenant
 * `X-API-Key` stays server-side and is never exposed to the browser.
 */

import { sharedApiClient, type PostFormDataOptions } from './client';
import { generateService } from './generate.service';

/** One word boundary in the synthesised audio (seconds from the start). */
export interface SpeechTimingWord {
    word: string;
    /** Normalised key for the word (agent-provided), e.g. "hello". */
    slug: string;
    start: number;
    end: number;
}

/** Response from the speech-timing generate endpoint. */
export interface SpeechTimingResult {
    text: string;
    transcript: string;
    audio_url: string;
    audio_format: string;
    mime_type: string;
    language: string;
    /** 0-based index of the voice used. */
    voice?: number;
    /** Human-readable voice name (e.g. "Kore"). */
    voice_name?: string;
    word_timestamps: SpeechTimingWord[];
}

/** TTS voice catalog (GET /speech-timing/voices). */
export interface SpeechTimingVoicesResult {
    /** Voice names, indexed 0-based (index = position in this array). */
    voices: string[];
    count: number;
    default_index: number;
    default_name: string;
}

/** Request params. `text` and `file` are mutually exclusive (validated server-side). */
export interface SpeechTimingGenerateParams {
    /** Script text (<= 500 chars). Provide this OR `file`, not both. */
    text?: string;
    /** A `.txt` or `.docx` document to extract the script from. Provide this OR `text`. */
    file?: File | Blob;
    /** TTS language. Defaults to `en`. */
    language?: string;
    /** 0-based Gemini TTS voice index; omit → agent default (Kore). */
    voice?: number;
    /** Admin (staff JWT) callers with no bound tenant must pass this. */
    tenantId?: string;
}

class SpeechTimingService {
    private basePath = '/content/speech-timing';

    /**
     * Fetch the TTS voice catalog to bind the editor's voice dropdown.
     * Backed by a synchronous aihub gateway read (not a job).
     */
    async listVoices(params?: { tenantId?: string }): Promise<SpeechTimingVoicesResult> {
        const query = params?.tenantId
            ? `?tenant_id=${encodeURIComponent(params.tenantId)}`
            : '';
        const response = await sharedApiClient.get<SpeechTimingVoicesResult>(
            `${this.basePath}/voices${query}`
        );
        return response.data;
    }

    /**
     * Synthesise TTS audio + word timing for a reading script.
     * Starts the aihub job, polls until it finishes, and resolves with the
     * agent result. The editor awaits this to preview the audio and store the
     * stimulus.
     */
    async generate(
        params: SpeechTimingGenerateParams,
        options?: PostFormDataOptions
    ): Promise<SpeechTimingResult> {
        const form = new FormData();
        const text = (params.text ?? '').trim();
        if (text) {
            form.append('text', text);
        }
        if (params.file) {
            form.append('file', params.file);
        }
        form.append('language', params.language || 'en');
        if (typeof params.voice === 'number') {
            form.append('voice', String(params.voice));
        }
        // Staff/superadmin JWTs carry no tenant; the caller passes the host tenant
        // (from the editor's tenantId prop) so the backend can resolve context.
        if (params.tenantId) {
            form.append('tenant_id', params.tenantId);
        }

        const startResponse = await sharedApiClient.postFormData<{
            job_id: string;
            status: string;
        }>(`${this.basePath}/generate`, form, options);
        const jobId = startResponse.data.job_id;

        // Poll the aihub job. TTS synthesis is typically a few seconds; allow
        // 2s × 90 = 180s, and fail fast if the worker never picks it up.
        const finalJob = await generateService.pollJobStatus(jobId, () => undefined, {
            interval: 2000,
            maxAttempts: 90,
            pendingTimeoutMs: 30_000,
        });

        if (finalJob.status === 'failed') {
            throw new Error(finalJob.error || 'Speech-timing generation failed');
        }

        return (finalJob.result ?? {}) as unknown as SpeechTimingResult;
    }
}

export const speechTimingService = new SpeechTimingService();
